import React, { useMemo, useState } from 'react';
import { Plots, errorMessage } from '../../api/client';
import { Plot, PlotRef, PlotSnapshot } from '../../types';
import { Icon } from '../ui/Icon';
import { Empty, Loading, Note, Section } from '../ui/primitives';
import { fmtBytes, fmtDateTime, timeAgo } from '../../utils/format';

interface Props {
  plotRef: PlotRef;
  plot: Plot;
  snapshots: PlotSnapshot[] | null;
  error: string | null;
  /** A snapshot was taken; the list is refetched. */
  onChanged: () => void;
}

/** Snapshots tab: the plot's snapshots (newest first) with .schem downloads, and "Snapshot now". */
const PlotSnapshotsTab: React.FC<Props> = ({ plotRef, plot, snapshots, error, onChanged }) => {
  const [snapshotting, setSnapshotting] = useState(false);
  const [snapshotError, setSnapshotError] = useState<string | null>(null);
  const [snapshotNotice, setSnapshotNotice] = useState<string | null>(null);

  const sorted = useMemo(() => [...(snapshots || [])].sort((a, b) => b.createdAt - a.createdAt), [snapshots]);

  const snapshot = async () => {
    if (snapshotting) return;
    setSnapshotting(true);
    setSnapshotError(null);
    setSnapshotNotice(null);
    try {
      const res = await Plots.snapshotNow(plotRef);
      setSnapshotNotice(
        res.created
          ? `Snapshot taken at ${fmtDateTime(res.createdAt)}.`
          : `Nothing has changed since the latest snapshot (${fmtDateTime(res.createdAt)}), so that one was kept.`
      );
      onChanged();
    } catch (e) {
      setSnapshotError(errorMessage(e, 'Failed to take a snapshot. Please try again.'));
    } finally {
      setSnapshotting(false);
    }
  };

  const snapshotButton = (label: string, className: string) => (
    <button type="button" className={className} disabled={snapshotting} title="Save the plot as it is right now" onClick={snapshot}>
      {snapshotting ? <span className="spin" style={{ borderTopColor: '#fff' }} /> : <Icon name="plus" size={12} />}
      {snapshotting ? 'Taking snapshot…' : label}
    </button>
  );

  return (
    <Section
      title="Snapshots"
      blurb="When plot is active with the owner inside, and the plot has changed, a snapshot is taken. You can revert a plot to a snapshot state in-game using '/psnapshot' command. You can also take a snapshot manually."
      actions={snapshotButton('Snapshot now', 'btn btn--sm btn--primary')}
    >
      {snapshotting && <Note icon="clock">Taking a snapshot of plot {plot.plotId}. This can take a minute or two.</Note>}
      {snapshotError && <Note tone="danger">{snapshotError}</Note>}
      {snapshotNotice && <Note tone="ok">{snapshotNotice}</Note>}
      {error && <Note tone="danger">{error}</Note>}

      {snapshots == null && !error ? (
        <Loading />
      ) : sorted.length === 0 ? (
        !error && (
          <Empty icon="backup" title="No snapshots yet" action={snapshotButton('Take first snapshot', 'btn btn--primary')}>
            Take one now, or build something and the server snapshots the plot automatically.
          </Empty>
        )
      ) : (
        sorted.map((s, i) => (
          <div className="lrow" key={s.createdAt}>
            <div className="lrow-main">
              <span className="bkico">
                <Icon name="backup" size={15} />
              </span>
              <span>
                <b style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  {fmtDateTime(s.createdAt)}
                  {i === 0 && (
                    <span className="pill pill--online" style={{ fontSize: 10.5, padding: '2px 8px' }}>
                      latest
                    </span>
                  )}
                </b>
                <small>
                  {timeAgo(s.createdAt)} · {fmtBytes(s.size)}
                </small>
              </span>
            </div>
            <div className="lrow-act">
              <a className="btn btn--sm btn--ghost" href={Plots.snapshotDownloadUrl(plotRef, s.createdAt)} download title="Download as a WorldEdit .schem">
                <Icon name="download" size={14} /> Download .schem
              </a>
            </div>
          </div>
        ))
      )}
    </Section>
  );
};

export default PlotSnapshotsTab;
